import dayjs from 'dayjs';
import { type AggregatedMetrics } from '@prerender/prerender-shared/FE';

import { restApi } from '~features/api/restApiSlice';
import { AGGREGATED_METRICS_API_SHARED_SETTINGS } from '~features/api/aggregatedMetricsApiSettings';
import { type CrawlerCategoryScheme, deCategorizeCrawlersList } from '~features/crawler-filter';
import {
  type CDNAnalyticQueryParams,
  type CDNAnalyticsCrawlerRequestsOutput,
  type CDNAnalyticsCrawlerVisitsOutput,
  QueryParamNames,
} from './QueryParameters';
import { PageCrawl } from './PageCrawl';

export type CDNAnalyticsExportStatus = 'queued' | 'not queued' | 'already exists';

type CrawlerRequestsQueryParams = CDNAnalyticQueryParams & { categoryScheme: CrawlerCategoryScheme };

type CrawlerVisitsQueryParams = AggregatedMetrics.BaseGraphQueryParams & { categoryScheme: CrawlerCategoryScheme };

// TABLE
export function buildCrawlerRequestsParams(input: CrawlerRequestsQueryParams) {
  const { categoryScheme, ...wireInput } = input;
  const output: Partial<CDNAnalyticsCrawlerRequestsOutput> = {
    date_from: dayjs(wireInput.dateFrom).startOf('day').toISOString(),
    date_to: dayjs(wireInput.dateTo).endOf('day').toISOString(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  Object.keys(wireInput).forEach((key) => {
    switch (key) {
      case QueryParamNames.Crawlers:
        if (wireInput.crawlers && wireInput.crawlers.length > 0) {
          output[key] = deCategorizeCrawlersList(wireInput.crawlers, categoryScheme).join(',');
        }
        break;

      case QueryParamNames.DateFrom:
      case QueryParamNames.DateTo:
        // Already handled above
        break;

      case QueryParamNames.Domain:
        if (wireInput[key]) {
          output[key] = wireInput[key];
        }
        break;

      case QueryParamNames.AdaptiveType:
      case QueryParamNames.CacheHit:
        output[key] = wireInput[key]?.join(',');
        break;

      default:
        (output as any)[key] = wireInput[key as keyof CDNAnalyticQueryParams];
        break;
    }
  });
  return output;
}

// CHARTS
export function buildCrawlerVisitsParams(input: CrawlerVisitsQueryParams) {
  const output: Partial<CDNAnalyticsCrawlerVisitsOutput> = {
    dateFrom: dayjs(input.dateFrom).startOf('day').toISOString(),
    dateTo: dayjs(input.dateTo).endOf('day').toISOString(),
    firstDayOfWeek: dayjs().localeData().firstDayOfWeek(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  if (input.domain) output.domain = input.domain;
  if (input.crawlers && input.crawlers.length > 0) {
    output.crawlers = deCategorizeCrawlersList(input.crawlers, input.categoryScheme);
  }

  return output;
}

type PageCrawlsResponse = {
  data: PageCrawl[];
  totalCount: number;
};

export const CRAWLED_PAGES_ENDPOINT = '/v3/analytics/crawled-pages';

const CDNAnalyticsAPI = restApi.injectEndpoints({
  endpoints: (builder) => ({
    getResponseTime: builder.query<AggregatedMetrics.CacheHitMissTimeResult, CrawlerVisitsQueryParams>({
      query: (input) => {
        const queryParams = buildCrawlerVisitsParams(input);
        return {
          url: '/v4/aggregated-metrics/cache-hit-miss-time',
          params: queryParams,
        };
      },
      keepUnusedDataFor: AGGREGATED_METRICS_API_SHARED_SETTINGS.keepUnusedDataFor,
    }),
    getVolume: builder.query<AggregatedMetrics.PagesCrawledVolumeResult, CrawlerVisitsQueryParams>({
      query: (input) => {
        const queryParams = buildCrawlerVisitsParams(input);
        return {
          url: '/v4/aggregated-metrics/pages-crawled-volume',
          params: queryParams,
        };
      },
      keepUnusedDataFor: AGGREGATED_METRICS_API_SHARED_SETTINGS.keepUnusedDataFor,
    }),
    getCrawlerRequests: builder.query<PageCrawlsResponse, CrawlerRequestsQueryParams>({
      query: (params) => {
        const queryParams = buildCrawlerRequestsParams(params);
        return {
          url: CRAWLED_PAGES_ENDPOINT,
          params: queryParams,
        };
      },
    }),
    exportCrawlerRequests: builder.mutation<{ status: CDNAnalyticsExportStatus }, CrawlerRequestsQueryParams>({
      query: (params) => {
        const queryParams = buildCrawlerRequestsParams(params);
        return {
          url: '/v3/analytics/crawled-pages/export',
          method: 'POST',
          body: queryParams,
        };
      },
    }),
  }),
});

export const {
  useGetCrawlerRequestsQuery,
  useGetResponseTimeQuery,
  useGetVolumeQuery,
  useExportCrawlerRequestsMutation,
} = CDNAnalyticsAPI;
